import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';
import { useRouter } from 'next/router';

import { Box, Divider, ErrorBoundary, Stack, useDeviceType } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { usePaging } from '../../hooks/usePaging';
import type { SearchApiResponse } from '../../api/rest/types';
import { InfiniteProductGrid } from '../InfiniteProductGrid';
import { ProductGrid } from '../ProductGrid';
import { ProductListPagination } from '../ProductListPagination';
import type { FilterSettingsProps } from '../FilterSettings';
import { FilterSettings } from '../FilterSettings';
import { SearchAdServerIntegration } from '../SearchAdServerIntegration';
import { DisplaySettingsDesktop } from '../DisplaySettingsDesktop';
import { DisplaySettingsMobile } from '../DisplaySettingsMobile';

export const ProductListPageLayoutFragment = graphql(/* GraphQL */ `
  fragment ProductListPageLayoutFragment on SearchProductResult {
    ...FilterSettingsFragment
    ...UsePagingFragment
    ...DisplaySettingsDesktopFragment
    ...DisplaySettingsMobileFragment
    ...ProductListPaginationFragment
    ...ProductGridFragment
    ...InfiniteProductGridFragment
  }
`);

export type ProductListPageLayoutProps = {
  maskedData: FragmentType<typeof ProductListPageLayoutFragment>;
  apiResponse: SearchApiResponse;
  topLevelCategories?: string[];
  aboveMainContentComponent: ReactNode;
  leftSidebarDesktopComponent: ReactNode;
  renderCategoryDialogContent: FilterSettingsProps['renderCategoryDialogContent'];
  belowProductsComponent: ReactNode;
  referrer?: string;
};

/**
 * Shared layout for pages displaying product lists
 *
 * Includes the actual product grid, filtering, sorting, display mode, pagination, and slots for additional content.
 */
export const ProductListPageLayout: FC<ProductListPageLayoutProps> = ({
  maskedData,
  apiResponse,
  topLevelCategories,
  aboveMainContentComponent,
  leftSidebarDesktopComponent,
  renderCategoryDialogContent,
  belowProductsComponent,
  referrer,
}) => {
  const data = getFragmentData(ProductListPageLayoutFragment, maskedData);

  const { isMobile, isDesktop } = useDeviceType();
  const { asPath } = useRouter();

  const { pagingMode, pageCount, currentPage } = usePaging(data);

  const { requestModification } = apiResponse.searchresult.information;

  return (
    // position relative is necessary for the SearchAdServerIntegration component
    <Stack gap={1} padding={1} position="relative">
      {aboveMainContentComponent}

      <FilterSettings
        maskedData={data}
        showRemovedFiltersNotification={requestModification?.removedFilters}
        renderCategoryDialogContent={renderCategoryDialogContent}
      />

      {isDesktop ? (
        <DisplaySettingsDesktop maskedData={data} />
      ) : (
        <DisplaySettingsMobile maskedData={data} />
      )}

      <Divider />

      <Stack direction="row" gap={1}>
        {!isMobile && (
          <Box sx={{ width: { md: '25%', lg: '20%' } }}>{leftSidebarDesktopComponent}</Box>
        )}

        <Stack
          gap={2}
          sx={{
            flex: 1,
            // NOTE: minWidth is required to properly propagate size constraints to RecoSlider and prevent the page from blowing up
            // see also: https://github.com/philipwalton/flexbugs#1-minimum-content-sizing-of-flex-items-not-honored
            minWidth: 0,
          }}
        >
          {pagingMode === 'infinite' ? (
            <InfiniteProductGrid
              maskedResult={data}
              key={asPath}
              referrer={referrer}
              topLevelCategories={topLevelCategories}
            />
          ) : (
            <ProductGrid
              maskedResult={data}
              key={asPath}
              pageNumberForTracking={currentPage}
              topLevelCategories={topLevelCategories}
            />
          )}
          {pageCount > 1 && pagingMode === 'limited' && (
            <Box
              sx={{
                alignSelf: 'center',
              }}
            >
              <ProductListPagination maskedData={data} />
            </Box>
          )}
          {belowProductsComponent}
        </Stack>
      </Stack>

      <ErrorBoundary>
        <Suspense>
          <SearchAdServerIntegration majorMkz={apiResponse?.searchresult?.result?.majorMkz} />
        </Suspense>
      </ErrorBoundary>
    </Stack>
  );
};
